import {
  adminV1OrganizationsProfilesCreate,
  adminV1OrganizationsProfilesList,
  adminV1OrganizationsProfilesPartialUpdate,
  adminV1OrganizationsProfilesRetrieve,
  adminV1OrganizationsProfilesDestroy,
  adminV1OrganizationsProfilesSmsConsentResetCreate,
  adminV1OrganizationsProfilesSplitSharedAccountCreate,
} from "@/api/admin-profiles/admin-profiles";

import type {
  AdminV1OrganizationsProfilesListParams,
  PatchedAdminProfile,
} from "@/api/model";

import {
  adminV1OrganizationsCoachesDestroy,
  adminV1OrganizationsCoachesCreate,
  adminV1OrganizationsCoachesPartialUpdate,
  adminV1OrganizationsCoachesList,
} from "@/api/admin-coaches/admin-coaches";
import {
  adminV1OrganizationsProfilesAssociatedContactsCreate,
  adminV1OrganizationsProfilesAssociatedContactsDestroy,
  adminV1OrganizationsProfilesAssociatedContactsList,
  adminV1OrganizationsProfilesAssociatedContactsPartialUpdate,
} from "@/api/admin-profile-associated-contacts/admin-profile-associated-contacts";
import type {
  AdminProfile,
  PatchedAdminCoach,
  AdminProfileAssociatedContact,
  AdminV1OrganizationsAssociatedContactsListParams,
} from "../../api/model";
import {
  adminV1OrganizationsAssociatedContactsAddProfileCreate,
  adminV1OrganizationsAssociatedContactsList,
} from "@/api/admin-associated-contacts/admin-associated-contacts";
import { useImpersonationStore } from "@/modules/auth/stores/impersonation";
import { useCurrentOrganization } from "@/composables/useCurrentOrganization";
import http from "@/shared/services/api-client";

import { useNetworkOrganizations } from "@/modules/networkAdmin/composables/useNetworkOrganizations";

function getDefaultOrganizationId(): number {
  const { currentOrganizationId } = useCurrentOrganization();
  const orgId = currentOrganizationId.value;
  if (!orgId) {
    throw new Error("No default organization found");
  }
  return orgId;
}

function getOrganizationId(organizationId?: number): number {
  return organizationId || getDefaultOrganizationId();
}

export const createUser = async (
  form: AdminProfile,
  selectedOrganizationId?: number
) => {
  try {
    const organizationId = getOrganizationId(selectedOrganizationId);
    const result = await adminV1OrganizationsProfilesCreate(
      organizationId,
      form
    );
    return result;
  } catch (e: any) {
    // The axios client rejects 409s as { status, data }, not an Error —
    // e.message would be undefined. Same special-casing as useCreateUser.
    const conflict = e?.status === 409;
    return {
      error: conflict
        ? e?.data?.detail ||
          "This person already has a profile in this organization."
        : e.message,
      conflict,
    };
  }
};

export const deleteUser = async (
  userId: number,
  selectedOrganizationId?: number
) => {
  try {
    const organizationId = getOrganizationId(selectedOrganizationId);
    const result = await adminV1OrganizationsProfilesDestroy(
      organizationId,
      userId
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getAllMembers = async (
  resultsPerPage: number,
  offset: number,
  teamId: number
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsProfilesList(organizationId, {
      limit: resultsPerPage,
      offset: offset,
      team_id: teamId,
    });
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getAllUsers = async (
  args: AdminV1OrganizationsProfilesListParams & { organizationId?: number }
) => {
  try {
    // Explicit org, else the current context (the impersonated org for TFA employees)
    const { organizationId, ...params } = args;
    return await adminV1OrganizationsProfilesList(
      getOrganizationId(organizationId),
      params
    );
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getUser = async (userId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsProfilesRetrieve(
      organizationId,
      userId
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateUser = async (
  userId: number,
  form: PatchedAdminProfile,
  selectedOrganizationId?: number
) => {
  try {
    const organizationId = getOrganizationId(selectedOrganizationId);
    const result = await adminV1OrganizationsProfilesPartialUpdate(
      organizationId,
      userId,
      form
    );
    return result;
  } catch (e: any) {
    // Surface the backend's message (e.g. role-change guards) over the
    // generic axios one.
    return { error: e?.data?.roles || e?.data?.error || e.message };
  }
};

export const restoreUser = async (userId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsProfilesPartialUpdate(
      organizationId,
      userId,
      {
        is_active: true,
      }
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const removeAsCoach = async (
  coachId: number,
  selectedOrganizationId?: number
) => {
  try {
    const organizationId = getOrganizationId(selectedOrganizationId);
    const result = await adminV1OrganizationsCoachesDestroy(
      organizationId,
      coachId
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const updateCoach = async (
  coachId: number,
  formCoach: PatchedAdminCoach,
  selectedOrganizationId?: number
) => {
  try {
    const organizationId = getOrganizationId(selectedOrganizationId);
    const result = await adminV1OrganizationsCoachesPartialUpdate(
      organizationId,
      coachId,
      formCoach
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const makeACoach = async (userId: number, organization_id: number) => {
  try {
    const organizationId = getOrganizationId(organization_id);
    const result = await adminV1OrganizationsCoachesCreate(organizationId, {
      profile_id: userId,
    });
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getCoaches = async (resultsPerPage: number, offset: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsCoachesList(organizationId, {
      limit: resultsPerPage,
      offset: offset,
    });
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const addContactToUser = async (
  profileId: number,
  form: Pick<
    AdminProfileAssociatedContact,
    "email" | "name" | "primary_contact" | "phone_number"
  > & {
    organization: number;
    profile: number;
    // Relationship type for the link; omitted defaults to true on new
    // links and is preserved on existing ones (409 self-heal path).
    is_parent?: boolean;
  }
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsProfilesAssociatedContactsCreate(
      organizationId,
      profileId,
      form
    );

    return result;
  } catch (e: any) {
    if (e.status && e.status == 409 && e.data) {
      const contactId = e.data?.id;

      try {
        const organizationId = getDefaultOrganizationId();
        await adminV1OrganizationsAssociatedContactsAddProfileCreate(
          organizationId,
          contactId,
          {
            profile: form.profile,
            primary_contact: form.primary_contact,
            is_parent: form.is_parent,
          }
        );

        return e.data.associated_contact;
      } catch (e: any) {
        return { error: e.message };
      }
    }
    return { error: e.message };
  }
};

export const updateAssociatedContactApi = async (
  profileId: number,
  contactId: number,
  form: object
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result =
      await adminV1OrganizationsProfilesAssociatedContactsPartialUpdate(
        organizationId,
        profileId,
        contactId,
        form
      );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const removeContactFromUser = async (
  profileId: number,
  contactId: number
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const result = await adminV1OrganizationsProfilesAssociatedContactsDestroy(
      organizationId,
      profileId,
      contactId
    );
    return result;
  } catch (e: any) {
    return { error: e.message };
  }
};

/**
 * Link an existing player to a contact. An omitted is_parent defaults to
 * true on a new link and is preserved on an existing one.
 */
export const linkPlayerToContact = async (
  organizationId: number,
  contactId: number,
  playerId: number,
  flags?: { is_parent?: boolean }
) => {
  const result = await adminV1OrganizationsAssociatedContactsAddProfileCreate(
    organizationId,
    contactId,
    {
      profile: playerId,
      primary_contact: false,
      ...flags,
    }
  );
  return result;
};

export const splitSharedAccount = async (
  profileId: number,
  playerEmail: string,
  selectedOrganizationId?: number
) => {
  try {
    const organizationId = getOrganizationId(selectedOrganizationId);
    const result = await adminV1OrganizationsProfilesSplitSharedAccountCreate(
      organizationId,
      profileId,
      { player_email: playerEmail }
    );
    return result;
  } catch (e: any) {
    const errorMessage =
      e?.data?.error || e?.message || "Failed to split shared account";
    return { error: errorMessage };
  }
};

export const getAssociatedContacts = async (profileId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response = await adminV1OrganizationsProfilesAssociatedContactsList(
      organizationId,
      profileId
    );
    return response;
  } catch (e: any) {
    return { error: e.message };
  }
};

export const getOrganizationAssociatedContacts = async (
  params: AdminV1OrganizationsAssociatedContactsListParams
) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const response = await adminV1OrganizationsAssociatedContactsList(
      organizationId,
      params
    );
    return response;
  } catch (e: any) {
    return { error: e.message };
  }
};

export interface EnsureSendbirdUserResult {
  already_existed: boolean;
  was_created: boolean;
  sendbird_user_id: string;
}

export interface EnsurePayabliCustomerResult {
  already_existed: boolean;
  was_created: boolean;
  payabli_customer_id: number;
  billing_profile_id: string;
}

// Hand-written against the ensure-provisioning endpoints (MAI-5051) until the
// orval client is regenerated. Staff-only, create-only: an already-provisioned
// profile is reported as already_existed and never resynced.
export const ensureSendbirdUser = async (profileId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const { data } = await http.post<EnsureSendbirdUserResult>(
      `/api/admin/v1/organizations/${organizationId}/profiles/${profileId}/ensure-sendbird-user/`
    );
    return data;
  } catch (e: any) {
    return {
      error:
        e?.response?.data?.error ||
        e?.message ||
        "Failed to ensure Sendbird user",
    };
  }
};

export const ensurePayabliCustomer = async (profileId: number) => {
  try {
    const organizationId = getDefaultOrganizationId();
    const { data } = await http.post<EnsurePayabliCustomerResult>(
      `/api/admin/v1/organizations/${organizationId}/profiles/${profileId}/ensure-payabli-customer/`
    );
    return data;
  } catch (e: any) {
    return {
      error:
        e?.response?.data?.error ||
        e?.message ||
        "Failed to ensure Payabli customer",
    };
  }
};

/** Records new written texting consent for a profile that opted out; Twilio's own STOP block stays until they text START. */
export const resetSmsConsent = (
  profileId: number,
  selectedOrganizationId?: number
) =>
  adminV1OrganizationsProfilesSmsConsentResetCreate(
    getOrganizationId(selectedOrganizationId),
    profileId
  );
